import { Link, usePage } from '@inertiajs/react'
import styles from './Tabs.module.css'

export interface TabItem<T extends string = string> {
  value: T
  label: string
  /* Route mode: navigating href. */
  href?: string
  /* Extra URL prefix that also counts as "here" (route mode). */
  match?: string
}

interface ControlledProps<T extends string> {
  items: TabItem<T>[]
  value: T
  onChange: (value: T) => void
  size?: 'sm' | 'md'
  disabled?: boolean
  'aria-label'?: string
}

interface RouteProps<T extends string> {
  items: TabItem<T>[]
  value?: undefined
  onChange?: undefined
  size?: 'sm' | 'md'
  disabled?: boolean
  'aria-label'?: string
}

export type TabsProps<T extends string> = ControlledProps<T> | RouteProps<T>

function useRouteActive<T extends string>(items: TabItem<T>[]): T | undefined {
  const { url } = usePage()
  const current = url.split('?')[0]
  // Longest matching root wins, so "/blackjack" (Play) does not light up on
  // "/blackjack/train".
  const score = (item: TabItem<T>) =>
    Math.max(0, ...[item.href, item.match].filter(Boolean).map((r) => (current === r || current.startsWith(`${r}/`) ? (r as string).length : 0)))
  const best = Math.max(0, ...items.map(score))
  return best > 0 ? items.find((i) => score(i) === best)?.value : undefined
}

/* Segmented navigation. Route mode (items with href) highlights the current
   page; controlled mode (value/onChange) switches in-page content. */
export function Tabs<T extends string>({ items, value, onChange, size = 'md', disabled, ...rest }: TabsProps<T>) {
  const routeActive = useRouteActive(items)
  const active = value ?? routeActive
  return (
    <nav className={[styles.bar, size === 'sm' && styles.sm].filter(Boolean).join(' ')} role={onChange ? 'tablist' : undefined} {...rest}>
      {items.map((item) => {
        const isActive = item.value === active
        const cls = [styles.tab, isActive && styles.active].filter(Boolean).join(' ')
        if (item.href && !onChange) {
          return (
            <Link key={item.value} href={item.href} className={cls} aria-current={isActive ? 'page' : undefined}>
              {item.label}
            </Link>
          )
        }
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={cls}
            disabled={disabled}
            onClick={() => onChange?.(item.value)}
          >
            {item.label}
          </button>
        )
      })}
    </nav>
  )
}

export const BLACKJACK_TABS: TabItem[] = [
  { value: 'play', href: '/blackjack/play', label: 'Play', match: '/blackjack' },
  { value: 'train', href: '/blackjack/train', label: 'Trainer' },
  { value: 'strategy', href: '/blackjack/strategy', label: 'Strategy' },
]

export const POKER_TABS: TabItem[] = [
  { value: 'play', href: '/poker/games/new', label: 'Play', match: '/poker/games' },
  { value: 'strategy', href: '/poker/strategy', label: 'Strategy' },
]

export const HOLDEM_TABS: TabItem[] = [
  { value: 'play', href: '/holdem/games/new', label: 'Play', match: '/holdem/games' },
  { value: 'online', href: '/holdem/rooms', label: 'Online', match: '/holdem/rooms' },
  { value: 'bots', href: '/holdem/bots', label: 'Bots' },
]

/* Bots are invisible unless the account may edit them. */
export function holdemTabs(canEditBots: boolean): TabItem[] {
  return canEditBots ? HOLDEM_TABS : HOLDEM_TABS.filter((t) => t.value !== 'bots')
}
